import { useMemo } from 'react';
import { FilterX, LibraryBig } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/states';
import { CatalogueToolbar } from '@/features/links/catalogue-toolbar';
import { FilterChips, buildFilterChips } from '@/features/links/filter-chips';
import { LinkCard } from '@/features/links/link-card';
import { LinkListSkeleton } from '@/features/links/link-skeletons';
import { LinkRow } from '@/features/links/link-row';
import { useCatalogueView } from '@/features/links/view-toggle';
import { toFacetOptions, type FacetOption } from '@/features/links/facet-filter';
import { useCatalogueFilters } from '@/hooks/use-catalogue-filters';
import { useCategoriesQuery, useCategoryLabels } from '@/hooks/use-categories';
import { useFacetQuery } from '@/hooks/use-facets';
import { useLinksQuery } from '@/hooks/use-links';
import { useIsAdmin } from '@/lib/current-user';
import { hasActiveFilters } from '@/lib/filters';
import { pluralize } from '@/lib/format';
import type { FacetValue, LinkSummary } from '@/types/api';

export function CataloguePage() {
  const { filters, patchFilters, removeFilter, resetFilters } = useCatalogueFilters();
  const [view, setView] = useCatalogueView();
  const isAdmin = useIsAdmin();

  const categories = useCategoriesQuery();
  const sharers = useFacetQuery('users');
  const channels = useFacetQuery('channels');
  const domains = useFacetQuery('domains');
  const links = useLinksQuery(filters);

  const categoryLabels = useCategoryLabels();

  const linkCount = (facet: FacetValue) => facet.linkCount;
  const sharerOptions = useMemo(() => toFacetOptions(sharers.data, linkCount), [sharers.data]);
  const channelOptions = useMemo(() => toFacetOptions(channels.data, linkCount), [channels.data]);
  const domainOptions = useMemo(() => toFacetOptions(domains.data, linkCount), [domains.data]);

  const categoryOptions = useMemo<FacetOption[]>(
    () =>
      (categories.data ?? [])
        .filter((category) => !category.archived || filters.categories.includes(category.slug))
        .map((category) => ({ value: category.slug, label: category.name })),
    [categories.data, filters.categories],
  );

  const chips = useMemo(
    () =>
      buildFilterChips(filters, {
        categoryLabels,
        sharerLabels: new Map((sharers.data ?? []).map((entry) => [entry.value, entry.label])),
        channelLabels: new Map((channels.data ?? []).map((entry) => [entry.value, entry.label])),
      }),
    [filters, categoryLabels, sharers.data, channels.data],
  );

  const items: LinkSummary[] = useMemo(
    () => (links.data?.pages ?? []).flatMap((page) => page.items),
    [links.data],
  );

  const filtered = hasActiveFilters(filters);
  const showSkeleton = links.isPending;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">Catalogue</h1>
        <p className="text-sm text-muted-foreground">
          Every unique link shared in Slack or added here, kept once and credited to whoever shared
          it first.
        </p>
      </header>

      <CatalogueToolbar
        filters={filters}
        patchFilters={patchFilters}
        view={view}
        onViewChange={setView}
        categoryOptions={categoryOptions}
        categoriesLoading={categories.isPending}
        sharerOptions={sharerOptions}
        sharersLoading={sharers.isPending}
        channelOptions={channelOptions}
        channelsLoading={channels.isPending}
        domainOptions={domainOptions}
        domainsLoading={domains.isPending}
        isAdmin={isAdmin}
      />

      <FilterChips chips={chips} onRemove={removeFilter} onClearAll={resetFilters} />

      {/* Async results are announced, not just rendered. */}
      <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
        {showSkeleton
          ? 'Loading links…'
          : links.isError
            ? 'Could not load links.'
            : `${items.length}${links.hasNextPage ? '+' : ''} ${pluralize(items.length, 'link')} shown.`}
      </p>

      {links.isError ? (
        <ErrorState
          title="Could not load the catalogue"
          error={links.error}
          onRetry={() => void links.refetch()}
        />
      ) : showSkeleton ? (
        <LinkListSkeleton view={view} />
      ) : items.length === 0 ? (
        filtered ? (
          <EmptyState
            icon={<FilterX aria-hidden="true" className="h-8 w-8" />}
            title="No links match these filters"
            description="Try removing a filter or widening the date range."
            action={
              <Button type="button" variant="outline" onClick={resetFilters}>
                Clear all filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<LibraryBig aria-hidden="true" className="h-8 w-8" />}
            title="No links have been collected yet"
            description="As soon as someone shares a link in a channel the bot has joined, it will show up here."
          />
        )
      ) : (
        <>
          <ul
            className={
              view === 'card'
                ? 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4'
                : 'flex flex-col gap-3'
            }
          >
            {items.map((link) => (
              <li key={link.linkId}>
                {view === 'card' ? (
                  <LinkCard link={link} categoryLabels={categoryLabels} />
                ) : (
                  <LinkRow link={link} categoryLabels={categoryLabels} />
                )}
              </li>
            ))}
          </ul>

          {/* Cursor pagination: the API hands back an opaque nextCursor, never an offset. */}
          <div className="flex justify-center pt-2">
            {links.hasNextPage ? (
              <Button
                type="button"
                variant="outline"
                loading={links.isFetchingNextPage}
                onClick={() => void links.fetchNextPage()}
              >
                {links.isFetchingNextPage ? 'Loading more…' : 'Load more'}
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                That is every link matching this view.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
